import { enforceConfiguration, DEFAULT_PROCTORING_CONFIGURATION_OPTIONS } from "@verity/proctoring-option";
import { getPlatformTextFromProctoringOptions } from "../proctoring-options";
import {
	MethodBlock,
	LiveSessionBlock,
	ProfileBlock,
	PlatformBlock,
	ConfigurationBlock,
	AccommodationBlock,
} from "./ConfirmationBlock";
import { EditQuizType, PendingLiveSession, VerityInstituteSettings } from "../..";
import { UseEditQuizNavigationReturn } from "../hooks/useEditQuizNavigation";
import { ProctoringDataType } from "../hooks/state/useSettingsState";
import { OptionRelatedInstituteSettings, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { EditQuizPageProps } from "../editQuiz";

type ConfirmationSummaryListProps = {
	numExempted?: number;
	pendingLiveSessions?: PendingLiveSession[];
	loading?: boolean;
	handleGoToStep: UseEditQuizNavigationReturn["handleGoToStep"];
	quizType: EditQuizType;
	verityInstituteSettings: VerityInstituteSettings;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	activeProfile?: ProctoringOptionProfileV2;
} & Pick<ProctoringDataType, "applications" | "extensions"> &
	Pick<EditQuizPageProps, "enforcedProfilesOnly" | "proctoringOption" | "proctoringAssessmentConfiguration">;

/**The core confirmation blocks. Method, Profile, Platform, Configuration, Accommodation */
export default function ConfirmationSummaryList({
	numExempted,
	pendingLiveSessions,
	loading,
	handleGoToStep,
	quizType,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	activeProfile,
	applications,
	extensions,
	enforcedProfilesOnly,
	proctoringOption,
	proctoringAssessmentConfiguration,
}: ConfirmationSummaryListProps) {
	return (
		<>
			{quizType !== "standalone" && !verityInstituteSettings?.liteMode && (
				<MethodBlock
					onEdit={enforcedProfilesOnly ? undefined : () => handleGoToStep("method")}
					proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
					loading={loading}
				/>
			)}

			{proctoringAssessmentConfiguration?.isLiveProctored && (
				<LiveSessionBlock
					pendingLiveSessions={pendingLiveSessions ?? []}
					proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
					onEdit={() => handleGoToStep("liveSessions")}
					loading={loading}
				/>
			)}
			{activeProfile && (
				<ProfileBlock profile={activeProfile} onEdit={() => handleGoToStep("profile")} loading={loading} />
			)}
			<PlatformBlock
				onEdit={enforcedProfilesOnly || activeProfile?.type === "preset" ? undefined : () => handleGoToStep("platform")}
				platformText={
					proctoringOption
						? getPlatformTextFromProctoringOptions(
								enforceConfiguration(
									DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
									proctoringOption,
									verityInstituteSettings,
									optionRelatedInstituteSettings,
								).proctoringOptions,
							)
						: undefined
				}
				loading={loading}
			/>

			<ConfigurationBlock
				onEdit={
					enforcedProfilesOnly || activeProfile?.type === "preset"
						? undefined
						: (category, proctoringOption) => handleGoToStep(category, proctoringOption)
				}
				proctoringOption={proctoringOption}
				verityInstituteSettings={verityInstituteSettings}
				optionRelatedInstituteSettings={optionRelatedInstituteSettings}
				applications={applications}
				extensions={extensions}
				loading={loading}
			/>

			<AccommodationBlock
				numExempted={numExempted ?? 0}
				onEdit={enforcedProfilesOnly ? undefined : () => handleGoToStep("accommodations")}
				loading={loading}
			/>
		</>
	);
}
